import { useState } from 'react';
import { useQuery, useMutation } from 'urql';
import { Q_INTEGRATIONS, M_UPSERT_INTEGRATION, M_DELETE_INTEGRATION } from '@/lib/queries';
import { useUi } from '@/stores/ui.store';

const CATALOG = [
  { provider: 'slack',         label: 'Slack',         icon: '💬', desc: 'Post notifications to a Slack channel via incoming webhook.', field: 'webhook_url', placeholder: 'https://hooks.slack.com/services/…' },
  { provider: 'webhook',       label: 'Outgoing webhook', icon: '🪝', desc: 'POST a JSON payload to your endpoint on each event.', field: 'webhook_url', placeholder: 'https://your-app.com/hooks/contenthub' },
  { provider: 'zapier',        label: 'Zapier',        icon: '⚡', desc: 'Trigger Zaps via a catch-hook URL.', field: 'webhook_url', placeholder: 'https://hooks.zapier.com/hooks/catch/…' },
  { provider: 'google_sheets', label: 'Google Sheets', icon: '📊', desc: 'Append a row per published post (via Apps Script webhook).', field: 'webhook_url', placeholder: 'https://script.google.com/macros/s/…/exec' },
];
const EVENTS = ['post.published', 'post.failed', 'inbox.message', 'approval.requested'];

export default function IntegrationsRoute() {
  const { toast } = useUi();
  const [integrations, refetch] = useQuery({ query: Q_INTEGRATIONS });
  const [, upsert] = useMutation(M_UPSERT_INTEGRATION);
  const [, remove] = useMutation(M_DELETE_INTEGRATION);

  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<{ label: string; url: string; events: string[] }>({ label: '', url: '', events: ['post.published'] });

  const configured: any[] = integrations.data?.integrations ?? [];
  const byProvider = (p: string) => configured.find((i) => i.provider === p);

  const openForm = (cat: typeof CATALOG[number]) => {
    const existing = byProvider(cat.provider);
    setForm({
      label: existing?.label ?? cat.label,
      url: existing?.config?.[cat.field] ?? '',
      events: existing?.events ?? ['post.published'],
    });
    setEditing(cat.provider);
  };

  const save = async (cat: typeof CATALOG[number]) => {
    if (!form.url.trim()) { toast('Webhook URL required', 'warning'); return; }
    const existing = byProvider(cat.provider);
    const r = await upsert({ input: {
      id: existing?.id ?? null, provider: cat.provider, label: form.label,
      config: { [cat.field]: form.url }, events: form.events,
    } });
    if (r.error) { toast(r.error.message, 'error'); return; }
    toast(`${cat.label} ${existing ? 'updated' : 'connected'}`);
    setEditing(null);
    refetch({ requestPolicy: 'network-only' });
  };

  const disconnect = async (id: string) => {
    await remove({ id });
    toast('Disconnected');
    refetch({ requestPolicy: 'network-only' });
  };

  const toggleEvent = (e: string) => setForm((f) => ({ ...f, events: f.events.includes(e) ? f.events.filter((x) => x !== e) : [...f.events, e] }));

  return (
    <div className="p-8 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-stone-900">Integrations</h1>
        <p className="text-sm text-stone-500 mt-1">Connect ContentHub to the tools your team already uses. Events fire as posts publish, fail, or arrive in your inbox.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {CATALOG.map((cat) => {
          const conn = byProvider(cat.provider);
          const isEditing = editing === cat.provider;
          return (
            <div key={cat.provider} className="bg-white border border-stone-200 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <span className="text-2xl">{cat.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-stone-900">{cat.label}</p>
                    {conn && <span className="text-[10px] uppercase font-medium px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">connected</span>}
                  </div>
                  <p className="text-xs text-stone-500 mt-0.5">{cat.desc}</p>
                </div>
                {!isEditing && (
                  <button onClick={() => openForm(cat)} className="px-3 py-1.5 text-xs bg-violet-600 hover:bg-violet-700 text-white rounded font-medium whitespace-nowrap">
                    {conn ? 'Edit' : 'Connect'}
                  </button>
                )}
              </div>

              {conn && !isEditing && (
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex flex-wrap gap-1">
                    {conn.events.map((e: string) => <span key={e} className="text-[9px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-500">{e}</span>)}
                  </div>
                  <button onClick={() => disconnect(conn.id)} className="text-[11px] text-red-500 hover:underline">Disconnect</button>
                </div>
              )}

              {isEditing && (
                <div className="mt-4 space-y-3 border-t border-stone-100 pt-4">
                  <label className="block">
                    <span className="text-xs font-medium text-stone-600">Label</span>
                    <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                      className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-stone-600">Webhook URL</span>
                    <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder={cat.placeholder}
                      className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
                  </label>
                  <div>
                    <span className="text-xs font-medium text-stone-600">Fire on</span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {EVENTS.map((e) => (
                        <button key={e} onClick={() => toggleEvent(e)}
                          className={`text-[11px] px-2 py-1 rounded ${form.events.includes(e) ? 'bg-violet-100 text-violet-700' : 'bg-stone-100 text-stone-500'}`}>
                          {e}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setEditing(null)} className="px-3 py-1.5 text-xs border border-stone-200 text-stone-600 rounded">Cancel</button>
                    <button onClick={() => save(cat)} className="px-4 py-1.5 text-xs bg-violet-600 hover:bg-violet-700 text-white rounded font-medium">Save</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-xs text-stone-400">Webhook payloads are signed and delivered by the notification worker. See <code>contenthub-workers</code>.</p>
    </div>
  );
}
